import React, { useCallback, useState } from 'react';
import Page from '@components/Page';
import { css } from '@emotion/react';
import { CventVideoEditor } from '@cvent/video-editor-js';
import { NoSsr } from '@components/NoSsr';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import {
  addApolloCacheProps,
  ApolloCacheProps,
  initApolloClient,
  SchemaLinkApolloClientFactory
} from '@cvent/apollo-client';
import { context, schema } from '@pages/api/graphql';
import { InMemoryCache } from '@apollo/client';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { Checkbox as CheckBox } from '@cvent/carina/components/Checkbox/Checkbox';
import NextLink from 'next/link';
import Button from '@cvent/carina/components/Button';
import Dropdown from '@cvent/carina/components/Dropdown/Dropdown';

import { isWhitelistedRegion } from 'src/utils';
import { MediaType, TextTrackKind } from '@schema/types';
import { TextTrackSourceInput } from '@pages/api/projects';
import { NavMetadata } from '@cvent/planner-navigation/types';
import { GET_APP_NAVIGATION_QUERY } from '@cvent/planner-navigation/client/queries';
import * as config from '../AppConfig';
import { createProjectWithOptions } from '../common/project';
import { AUTH_COOKIE_NAME, authClient, cookieManager } from '../common/authClient';

const LOG = LoggerFactory.create('src/pages/index');
const navMetadata: NavMetadata = {
  url: '/examples',
  staticRouteId: '/examples',
  topParentId: ''
};

type ExamplesPageProps = ApolloCacheProps & {
  projectId: string;
  videoEditorUrl: string;
  environment: string;
};

export async function getServerSideProps(
  serverSideContext: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<ExamplesPageProps>> {
  const client = initApolloClient({}, new SchemaLinkApolloClientFactory({}, schema, context), {
    cache: new InMemoryCache()
  });

  await client.query({
    query: GET_APP_NAVIGATION_QUERY,
    variables: { navMetadata }
  });

  const props = addApolloCacheProps(client, {});

  const videoId = (serverSideContext.query.videoId as string) || '7729b831-2211-40f9-9b58-6ea5a60aefd2';

  const environment = (serverSideContext.query.environment as string) || 'S606';

  const accountMappingId =
    (serverSideContext.query.accountMappingId as string) || 'd0808ab4-55fd-45e6-a888-283087dae98a';

  const userIds = ['test'];

  const textTrackLocale = (serverSideContext.query.textTrackLocale as string) || 'en-US';

  const textTrackKind = (serverSideContext.query.textTrackKind as TextTrackKind) || TextTrackKind.Subtitles;

  const cookies = cookieManager(serverSideContext);
  if (!cookies.getCookie(AUTH_COOKIE_NAME)) {
    try {
      const userToken = await authClient.createAccessToken({
        authorization: {
          metadata: {
            UserStub: 'test'
          }
        }
      });
      cookies.setCookie(AUTH_COOKIE_NAME, userToken?.accessToken);
    } catch (e) {
      LOG.error('Error creating cookie', e);
    }
  }

  const textTrackSource = {
    videoId,
    locale: textTrackLocale,
    kind: textTrackKind,
    type: MediaType.TextTrack,
    primary: true
  } as TextTrackSourceInput;

  const optionsRequest = {
    accountMappingId,
    userIds,
    options: {
      sources: [textTrackSource]
    }
  };

  const projectId = await createProjectWithOptions(optionsRequest, environment);

  if (!isWhitelistedRegion) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      ...props,
      projectId,
      videoEditorUrl: config.getVideoEditorEndpoint(),
      environment
    }
  };
}
type ThemeType = 'DARK' | 'DEFAULT';
type ModeType = 'VIDEO' | 'SUBTITLES';

const checkboxWrapperStyles = css`
  display: flex;
  gap: 30px;
`;

const checkboxLabelStyles = css`
  font-size: 16px;
`;

const buttonsWrapperStyles = css`
  display: flex;
  gap: 20px;
  width: 100%;
`;

const wrapperStyles = css`
  display: flex;
  justify-content: space-around;
  gap: 15px;
  width: 100%;
`;

const contentWrapperStyles = css`
  margin-top: 10px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 15px;
  border: 1px solid #7c7c7c;
  align-items: center;
  padding: 10px;
  width: 500px;
  align-self: center;
`;

export default function ExamplesPage({ videoEditorUrl, environment, projectId }: ExamplesPageProps): JSX.Element {
  const [mode, setMode] = useState<ModeType>('VIDEO');
  const [theme, setTheme] = useState<ThemeType>('DARK');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [video, setVideo] = useState(null);
  const videoOptions = [
    {
      label: 'Short video',
      value: '7729b831-2211-40f9-9b58-6ea5a60aefd2'
    },
    {
      label: 'Long video',
      value: 'c5930807-6af3-4e3a-b6b4-c2f83aaad162'
    }
  ];

  const toggleEditor = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleRenderComplete = useCallback((job): void => {
    // eslint-disable-next-line no-console
    console.log(`parent got event: ${JSON.stringify(job)}`);
  }, []);

  return (
    <Page navMetadata={navMetadata}>
      <div
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        <div css={contentWrapperStyles}>
          <span>Customize Project</span>
          <Dropdown
            options={videoOptions}
            selected={video}
            id="project"
            onUpdate={setVideo}
            placeholder="Select video"
          />

          <div css={wrapperStyles}>
            <div>
              <p css={checkboxLabelStyles}>Select theme</p>
              <div css={checkboxWrapperStyles}>
                <CheckBox id="darkTheme" onChange={(): void => setTheme('DARK')} checked={theme === 'DARK'}>
                  Dark
                </CheckBox>
                <CheckBox id="defaultTheme" onChange={(): void => setTheme('DEFAULT')} checked={theme === 'DEFAULT'}>
                  {' '}
                  Default
                </CheckBox>
              </div>
            </div>
            <div>
              <p css={checkboxLabelStyles}>Select mode</p>
              <div css={checkboxWrapperStyles}>
                <CheckBox id="video" onChange={(): void => setMode('VIDEO')} checked={mode === 'VIDEO'}>
                  Video
                </CheckBox>
                <CheckBox id="subtitles" onChange={(): void => setMode('SUBTITLES')} checked={mode === 'SUBTITLES'}>
                  Subtitles
                </CheckBox>
              </div>
            </div>
          </div>
          <div css={buttonsWrapperStyles}>
            <NextLink passHref href={`examples/?videoId=${video}`}>
              <Button
                text="Launch Modal"
                appearance="filled"
                size="l"
                variant="tertiary"
                onClick={(): void => setIsOpen(true)}
                css={css`
                  width: 100%;
                `}
                disabled={!video}
              />
            </NextLink>
            <NextLink
              passHref
              href={`inline/?videoId=${video}&environment=S606&theme=${theme}&display=inline&mode=${mode}`}
            >
              <Button
                text="Launch Inline"
                appearance="filled"
                size="l"
                variant="tertiary"
                css={css`
                  width: 100%;
                `}
                disabled={!video}
              />
            </NextLink>
          </div>
          <NoSsr>
            <CventVideoEditor
              mode={mode}
              url={videoEditorUrl}
              environment={environment}
              isOpen={isOpen}
              onClose={toggleEditor}
              onRenderComplete={handleRenderComplete}
              projectId={projectId}
              theme={theme}
              display="MODAL"
            />
          </NoSsr>
        </div>
      </div>
    </Page>
  );
}
